import React from 'react';
import { t } from '../context/LanguageContext';

const CATEGORY_LABELS = {
  food_safety_plan:    'Food Safety Plan',
  ssop:                'SSOP',
  sop:                 'SOP',
  prerequisite_program:'Prerequisite Program',
  haccp:               'HACCP',
  monitoring:          'Monitoring',
  corrective_action:   'Corrective Action',
  verification:        'Verification',
  training:            'Training',
  recall:              'Recall',
  pest_control:        'Pest Control',
  allergen:            'Allergen',
  crisis_management:   'Crisis Management',
  supplier:            'Supplier',
  contractor:          'Contractor',
  visitor:             'Visitor',
  vendor:              'Vendor',
  other:               'Other',
};

function fmt(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d) ? dateStr : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function userName(user) {
  if (!user) return null;
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || null;
}

// Returns a complete standalone HTML string for print/download
function buildPrintHtml(policy, version, displayName, displayContent, lang, allVersions = []) {
  const company = policy.Company;
  const addressParts = [
    company?.addressLine1,
    company?.addressLine2,
    [company?.city, company?.stateProvince].filter(Boolean).join(', '),
    company?.postalCode,
  ].filter(Boolean);

  const createdByVersion  = version?.CreatedBy;
  const publishedBy       = version?.PublishedBy;
  const policyCreatedBy   = policy.CreatedBy;

  const headerHtml = `
    <table width="100%" style="border-bottom:2px solid #1a1a2e;padding-bottom:12px;margin-bottom:0;border-collapse:collapse">
      <tr>
        <td width="40%" style="vertical-align:middle">
          <div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">
            ${CATEGORY_LABELS[policy.category] || policy.category}${policy.sqfChapterRef ? ` &mdash; SQF ${policy.sqfChapterRef}` : ''}
          </div>
          ${version ? `<div style="font-size:12px;color:#555">
            V${version.versionNumber}${(version.revisionDate || version.publishedAt) ? ` &nbsp;&mdash;&nbsp; ${version.revisionDate || fmt(version.publishedAt)}` : ''}
          </div>` : ''}
        </td>
        <td style="text-align:center;vertical-align:middle">
          ${company?.logoUrl ? `<img src="${company.logoUrl}" style="max-height:60px;max-width:140px;object-fit:contain;display:block;margin:0 auto" />` : ''}
        </td>
        <td width="40%" style="text-align:right;vertical-align:middle;font-size:12px;color:#555">
          ${company?.name ? `<div style="font-weight:600;font-size:13px">${company.name}</div>` : ''}
          ${addressParts.map(a => `<div>${a}</div>`).join('')}
        </td>
      </tr>
    </table>`;

  const auditHtml = `
    <div style="border-top:1px solid #ccc;margin-top:32px;padding-top:12px">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:8px;font-weight:600">
        Document Control
      </div>
      <table style="border-collapse:collapse;font-size:12px;color:#555;width:auto">
        ${(createdByVersion || policyCreatedBy) ? `<tr>
          <td style="padding:3px 16px 3px 0;font-weight:600">Created by</td>
          <td style="padding:3px 16px 3px 0">${userName(createdByVersion || policyCreatedBy) || '—'}</td>
          <td style="padding:3px 0">${fmt(version?.createdAt) || '—'}</td>
        </tr>` : ''}
        ${publishedBy ? `<tr>
          <td style="padding:3px 16px 3px 0;font-weight:600">Approved by</td>
          <td style="padding:3px 16px 3px 0">${userName(publishedBy)}</td>
          <td style="padding:3px 0">${fmt(version?.publishedAt) || '—'}</td>
        </tr>` : ''}
      </table>
      ${version?.notes ? `<div style="margin-top:10px;padding-top:8px;border-top:1px solid #ddd">
        <span style="font-size:10px;text-transform:uppercase;color:#888;font-weight:600">Revision Notes</span>
        <div style="margin-top:4px;color:#444">${version.notes}</div>
      </div>` : ''}
      ${allVersions.length > 1 ? `
      <div style="margin-top:16px;padding-top:12px;border-top:1px solid #ddd">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:600;margin-bottom:8px">Revision History</div>
        <table style="border-collapse:collapse;font-size:11px;width:100%">
          <thead>
            <tr style="border-bottom:1px solid #ccc">
              <th style="text-align:left;padding:3px 12px 3px 0;color:#555">Version</th>
              <th style="text-align:left;padding:3px 12px 3px 0;color:#555">Revision Date</th>
              <th style="text-align:left;padding:3px 12px 3px 0;color:#555">Published</th>
              <th style="text-align:left;padding:3px 12px 3px 0;color:#555">Approved By</th>
              <th style="text-align:left;padding:3px 0;color:#555">Notes</th>
            </tr>
          </thead>
          <tbody>
            ${allVersions.map(v => `<tr style="border-bottom:1px solid #eee${v.id === policy.activeVersionId ? ';background:#f0f7ff' : ''}">
              <td style="padding:4px 12px 4px 0;font-weight:600">V${v.versionNumber}${v.id === policy.activeVersionId ? ' ★' : ''}</td>
              <td style="padding:4px 12px 4px 0">${v.revisionDate || '—'}</td>
              <td style="padding:4px 12px 4px 0">${v.publishedAt ? fmt(v.publishedAt) : '—'}</td>
              <td style="padding:4px 12px 4px 0">${v.PublishedBy ? userName(v.PublishedBy) : '—'}</td>
              <td style="padding:4px 0;color:#555">${v.notes || '—'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>` : ''}
    </div>`;

  return `<!DOCTYPE html><html><head><title>${displayName}</title>
    <style>
      body { font-family: Arial, sans-serif; max-width: 820px; margin: 40px auto; padding: 0 24px; line-height: 1.65; color: #222; }
      h1,h2,h3 { color: #1a1a2e; }
      table { border-collapse: collapse; }
      td, th { padding: 4px 8px; }
      img { max-width: 100%; height: auto; display: block; }
      @media print { body { margin: 0; padding: 24px; } }
    </style></head>
    <body>${headerHtml}<div style="margin-top:20px">${displayContent || ''}</div>${auditHtml}</body></html>`;
}

export default function PolicyDocumentView({ policy, version, lang, viewLang, onViewLangChange, allVersions = [] }) {
  const isBilingual    = policy.documentLanguage === 'both';
  const displayName    = viewLang === 'es' && policy.nameEs ? policy.nameEs : policy.name;
  const displayContent = viewLang === 'es'
    ? (version?.contentEs || version?.content || '')
    : (version?.content || '');

  const company        = policy.Company;
  const publishedBy    = version?.PublishedBy;
  const createdByVer   = version?.CreatedBy;
  const policyCreated  = policy.CreatedBy;
  const effectiveCreator = createdByVer || policyCreated;

  const addressParts = company ? [
    company.addressLine1,
    company.addressLine2,
    [company.city, company.stateProvince].filter(Boolean).join(', '),
    company.postalCode,
  ].filter(Boolean) : [];

  const handleDownload = () => {
    const html = buildPrintHtml(policy, version, displayName, displayContent, lang, allVersions);
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div>
      {/* ── Download button row ── */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        {isBilingual && onViewLangChange && (
          <div className="btn-group btn-group-sm">
            <button
              className={`btn ${viewLang === 'en' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onViewLangChange('en')}
            >
              🇺🇸 EN
            </button>
            <button
              className={`btn ${viewLang === 'es' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onViewLangChange('es')}
            >
              🇪🇸 ES
            </button>
          </div>
        )}
        {!isBilingual && <div />}
        <button className="btn btn-outline-secondary btn-sm" onClick={handleDownload}>
          ⬇ {t('Download PDF', 'Descargar PDF', lang)}
        </button>
      </div>

      {/* ── Document header ── */}
      <div className="border rounded p-3 mb-3 bg-white">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'start', gap: 12 }}>
          {/* Left: category + ref badges + version/date */}
          <div>
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <span className="badge bg-secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                {CATEGORY_LABELS[policy.category] || policy.category}
              </span>
              {policy.sqfChapterRef && (
                <span className="badge bg-light text-dark border" style={{ fontSize: 10 }}>SQF {policy.sqfChapterRef}</span>
              )}
            </div>
            {version && (
              <div className="text-muted" style={{ fontSize: 13 }}>
                <strong>V{version.versionNumber}</strong>
                {(version.revisionDate || version.publishedAt) && (
                  <span className="ms-2 text-muted">
                    — {version.revisionDate || fmt(version.publishedAt)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Center: company logo — always rendered to hold the column */}
          <div style={{ textAlign: 'center' }}>
            {company?.logoUrl && (
              <img
                src={company.logoUrl}
                alt="logo"
                style={{ maxHeight: 56, maxWidth: 120, objectFit: 'contain' }}
              />
            )}
          </div>

          {/* Right: company name + address */}
          <div style={{ textAlign: 'right', fontSize: 12 }} className="text-muted">
            {company?.name && <div className="fw-semibold" style={{ fontSize: 13 }}>{company.name}</div>}
            {addressParts.map((a, i) => <div key={i}>{a}</div>)}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <style>{`.policy-doc-content img { max-width: 100%; height: auto; display: block; }`}</style>
      <div className="border rounded p-3 mb-3 bg-white" style={{ fontFamily: 'Georgia, serif', lineHeight: 1.7, fontSize: 15 }}>
        {displayContent
          ? <div className="policy-doc-content" dangerouslySetInnerHTML={{ __html: displayContent }} />
          : <p className="text-muted mb-0">{t('No content for this version.', 'Sin contenido para esta versión.', lang)}</p>
        }
      </div>

      {/* ── Audit trail ── */}
      {(effectiveCreator || publishedBy || version?.notes) && (
        <div className="border rounded p-3 bg-light" style={{ fontSize: 12 }}>
          <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 10, letterSpacing: 1 }}>
            {t('Document Control', 'Control de documentos', lang)}
          </div>
          <div className="d-flex flex-wrap gap-4 mb-0">
            {effectiveCreator && (
              <div>
                <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>
                  {t('Created by', 'Creado por', lang)}
                </div>
                <div className="fw-semibold">{userName(effectiveCreator)}</div>
                {version?.createdAt && (
                  <div className="text-muted">{fmt(version.createdAt)}</div>
                )}
              </div>
            )}
            {publishedBy && (
              <div>
                <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>
                  {t('Approved by', 'Aprobado por', lang)}
                </div>
                <div className="fw-semibold">{userName(publishedBy)}</div>
                {version?.publishedAt && (
                  <div className="text-muted">{fmt(version.publishedAt)}</div>
                )}
              </div>
            )}
          </div>
          {version?.notes && (
            <div className="mt-2 pt-2 border-top">
              <div className="text-muted" style={{ fontSize: 10, textTransform: 'uppercase' }}>
                {t('Revision Notes', 'Notas de revisión', lang)}
              </div>
              <div className="mt-1" style={{ color: '#444' }}>{version.notes}</div>
            </div>
          )}
          {allVersions.length > 1 && (
            <div className="mt-3 pt-3 border-top">
              <div className="text-muted fw-semibold mb-2" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                {t('Revision History', 'Historial de Revisiones', lang)}
              </div>
              <table className="table table-sm mb-0" style={{ fontSize: 11 }}>
                <thead className="table-light">
                  <tr>
                    <th>{t('Ver.', 'Ver.', lang)}</th>
                    <th>{t('Revision Date', 'Fecha de Revisión', lang)}</th>
                    <th>{t('Published', 'Publicado', lang)}</th>
                    <th>{t('Approved By', 'Aprobado Por', lang)}</th>
                    <th>{t('Notes', 'Notas', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {allVersions.map(v => (
                    <tr key={v.id} className={v.id === policy.activeVersionId ? 'table-primary' : ''}>
                      <td className="fw-semibold">
                        V{v.versionNumber}
                        {v.id === policy.activeVersionId && (
                          <span className="badge bg-primary ms-1" style={{ fontSize: 9 }}>{t('current', 'actual', lang)}</span>
                        )}
                      </td>
                      <td>{v.revisionDate || '—'}</td>
                      <td>{v.publishedAt ? fmt(v.publishedAt) : '—'}</td>
                      <td>{v.PublishedBy ? userName(v.PublishedBy) : '—'}</td>
                      <td className="text-muted">{v.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
